import { gql } from "@apollo/client";
import { add, subtract } from "@asmbl/shared/money";
import { TableCell, TableRow, Typography } from "@mui/material";
import makeStyles from "../../../../makeStylesV4Compat";
import clsx from "clsx";
import { Fragment, useState } from "react";
import {
  ActualRow_compCycleData as CompCycleData,
  ActualRow_employee as Employee,
  RecItemType,
} from "../../../../__generated__/graphql";
import { CompCycleDisplay } from "../../../../components/CompCycle/types";
import { formatBudget } from "../../../../models/Budget";
import {
  calculateRequestsFromReports,
  formatRequest,
  formatVariance,
} from "../../../../models/CompRecommendation";
import { DV_RED, GRAY_1, GRAY_7, GRAY_8 } from "../../../../theme";
import { BudgetNameCell } from "../../Budgets/Table/BudgetNameCell";
import { IndentationCells } from "../../Budgets/Table/Connectors";
import { LazyReportingActualTier } from "./LazyReportingActualTier";

const useStyles = makeStyles(() => ({
  reportCell: {
    width: "6rem",
    backgroundColor: GRAY_8,
  },
  reportCellText: {
    color: GRAY_1,
    fontWeight: 400,
    fontSize: "0.8125rem",
    lineHeight: "1.55",
    letterSpacing: "-0.25px",
  },
  alertText: { color: DV_RED },
  directAndIndirectReportsCell: { backgroundColor: GRAY_7 },
}));

type Props = {
  employee: Employee;
  organizationName: string;
  varianceDisplay: CompCycleDisplay;
  expandable: boolean;
  compCycleData: CompCycleData;
  indentation: IndentationCells;
  isLastRow: boolean;
};

export function ActualRow({
  employee,
  organizationName,
  varianceDisplay,
  expandable,
  compCycleData,
  indentation,
  isLastRow,
}: Props): JSX.Element {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  const reportingIndentation: IndentationCells =
    indentation.length > 0 && isLastRow
      ? [...indentation.slice(0, -1), "hidden", "visible"]
      : indentation.concat("visible");

  const reports = [...employee.directReports, ...employee.indirectReports];

  const bonus = {
    budgetKey: "bonus",
    budget: employee.compCycleBudget?.bonus ?? null,
    requests: calculateRequestsFromReports(reports, RecItemType.MERIT_BONUS),
  };

  const actual = calculateRequestsFromReports(
    reports,
    RecItemType.ACTUAL_RECURRING_BONUS
  );

  const actualCompany = calculateRequestsFromReports(
    reports,
    RecItemType.ACTUAL_RECURRING_BONUS_COMPANY
  );
  const actualCustom = calculateRequestsFromReports(
    reports,
    RecItemType.ACTUAL_RECURRING_BONUS_CUSTOM
  );

  const actualCustomValues =
    actualCompany && actualCustom
      ? add(actualCompany, actualCustom)
      : actualCompany
        ? actualCompany
        : actualCustom;

  const actualTotal = {
    budgetKey: "actualRecurringBonus",
    budget: employee.compCycleBudget?.actualRecurringBonus ?? null,
    requests:
      actual && actualCustomValues
        ? add(actual, actualCustomValues)
        : actual
          ? actual
          : actualCustomValues,
  };

  return (
    <>
      <TableRow>
        <BudgetNameCell
          compCycleId={compCycleData.id}
          employee={employee}
          indentation={indentation}
          expanded={isExpanded}
          expandable={expandable}
          setExpanded={setIsExpanded}
          type="compare"
          isLastRowCell={isLastRow}
        />
        <TableCell
          align="right"
          width="5%"
          className={clsx(
            classes.reportCell,
            classes.directAndIndirectReportsCell
          )}
        >
          {employee.directReports.length}
        </TableCell>
        <TableCell
          align="right"
          width="5%"
          className={clsx(
            classes.reportCell,
            classes.directAndIndirectReportsCell
          )}
        >
          {employee.indirectReports.length}
        </TableCell>

        {[
          ...(compCycleData.allowBonus ? [bonus] : []),
          ...(compCycleData.allowActualRecurringBonus ? [actualTotal] : []),
        ].map(({ budgetKey, budget, requests }) => {
          const variance =
            budget && requests ? subtract(requests, budget) : null;

          return (
            <Fragment key={budgetKey}>
              {/* budget */}
              <TableCell
                align="right"
                className={classes.reportCell}
                width="20%"
              >
                <Typography className={classes.reportCellText}>
                  {formatBudget(budget)}
                </Typography>
              </TableCell>

              {/* request */}
              <TableCell
                align="right"
                className={classes.reportCell}
                width="20%"
              >
                <Typography className={classes.reportCellText}>
                  {formatRequest(requests)}
                </Typography>
              </TableCell>

              {/* variance */}
              <TableCell
                align="right"
                className={classes.reportCell}
                width="20%"
              >
                <Typography
                  className={clsx(classes.reportCellText, {
                    [classes.alertText]: variance && variance.value > 0,
                  })}
                >
                  {formatVariance(varianceDisplay, budget, variance)}
                </Typography>
              </TableCell>
            </Fragment>
          );
        })}
      </TableRow>

      {isExpanded && (
        <LazyReportingActualTier
          compCycleData={compCycleData}
          employee={employee}
          organizationName={organizationName}
          varianceDisplay={varianceDisplay}
          indentation={reportingIndentation}
        />
      )}
    </>
  );
}

ActualRow.fragments = {
  employee: gql`
    fragment ActualRow_employee on Employee {
      id
      displayName
      user {
        id
        photoURL
      }
      activeEmployment {
        id
        jobTitle
      }
      compCycleBudget(compCycleId: $compCycleId) {
        compCycleId
        employeeId
        bonus
        actualRecurringBonus
      }
      directReports {
        id
        compRecommendation(compCycleId: $compCycleId) {
          subjectId
          compCycleId
          latestSubmittedItems {
            id
            submittedAt
            recommendationType
            annualCashEquivalent(currencyCode: $currencyCode)
          }
        }
      }
      indirectReports {
        id
        compRecommendation(compCycleId: $compCycleId) {
          subjectId
          compCycleId
          latestSubmittedItems {
            id
            recommendationType
            annualCashEquivalent(currencyCode: $currencyCode)
          }
        }
      }
    }
  `,
  compCycleData: gql`
    fragment ActualRow_compCycleData on CompCycle {
      id
      allowBonus
      allowActualRecurringBonus
    }
  `,
};
